import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import ReportApprovePage from './pages/ReportApprovePage'
import ReportEditPage from './pages/ReportEditPage'
import NewHireSelfPage from './pages/NewHireSelfPage'
import './App.css'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

// Password-reset receiver. We standardize on Supabase's token_hash link format:
//   {{ .SiteURL }}/reset-password?token_hash={{ .TokenHash }}&type=recovery
// (survives Microsoft Safe Links prefetch, unlike the /verify and #access_token
// flows). When that lands in the URL we verify the OTP, which establishes a
// recovery session and lets the user set a new password. Returns true on a
// successful recovery verification. Runs before routing so the app never flashes
// the logged-in view. The supabase client stays on the implicit flow.
async function consumeRecoveryToken() {
  const url = new URL(window.location.href)
  const tokenHash = url.searchParams.get('token_hash')
  const type = url.searchParams.get('type')
  if (!tokenHash || type !== 'recovery') return false

  let recovered = false
  try {
    const { error } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash })
    if (error) console.error('[auth] recovery verifyOtp failed:', error.message)
    else recovered = true
  } catch (e) {
    console.error('[auth] recovery verifyOtp threw:', e)
  }
  // Strip the one-time token out of the address bar / history regardless of outcome.
  url.searchParams.delete('token_hash')
  url.searchParams.delete('type')
  window.history.replaceState({}, '', url.pathname + url.search + url.hash)
  return recovered
}

// Shown at /reset-password when we get there WITHOUT an active recovery state —
// i.e. the link was missing/expired/already used (a successful verify is handled
// by the top-level isRecovery short-circuit before routing).
function ResetLinkInvalid() {
  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Crew Dispatch</div>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Reset link invalid or expired</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
            Please request a new password reset email and open the most recent link.
          </div>
          <button className="btn btn-primary" onClick={() => window.location.href = '/login'}>Back to sign in</button>
        </div>
      </div>
    </div>
  )
}

function ResetPasswordForm() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const handleReset = async (e) => {
    e.preventDefault()
    if (password !== confirm) { setError('Passwords do not match'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    if (error) setError(error.message)
    else setDone(true)
    setLoading(false)
  }

  if (done) return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Crew Dispatch</div>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Password updated!</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>You can now sign in with your new password.</div>
          <button className="btn btn-primary" onClick={() => window.location.href = '/'}>Go to app</button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Crew Dispatch</div>
        <div className="login-tagline">Set a new password</div>
        {error && <div className="login-error">{error}</div>}
        <form onSubmit={handleReset}>
          <div className="form-group">
            <label className="form-label">New password</label>
            <input className="form-input" type="password" value={password}
              onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" required />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm password</label>
            <input className="form-input" type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password" required />
          </div>
          <button className="btn btn-primary btn-full" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  )
}

// A signed-out click on an emailed deep link (/?tab=tm&ticket=..., /?tab=new-hires)
// keeps its target through sign-in instead of landing on the default tab.
function loginPath() {
  const { pathname, search } = window.location
  return search ? `/login?redirect=${encodeURIComponent(pathname + search)}` : '/login'
}

// Login route that honors a ?redirect= target (used by the authenticated
// report-approve deep link: an un-signed-in click is sent here, then back).
// Only same-origin relative paths are accepted to avoid open-redirect.
function LoginRoute({ session }) {
  const [params] = useSearchParams()
  const raw = params.get('redirect') || ''
  const safe = raw.startsWith('/') && !raw.startsWith('//') ? raw : '/'
  if (session) return <Navigate to={safe} replace />
  return <Login />
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isRecovery, setIsRecovery] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => {
    let active = true

    async function bootstrap() {
      // Resolve a password-reset link BEFORE reading the session, so we never
      // flash the logged-in app between session-restore and recovery detection.
      const recovered = await consumeRecoveryToken()
      if (!active) return
      if (recovered) setIsRecovery(true)
      const { data: { session } } = await supabase.auth.getSession()
      if (!active) return
      setSession(session)
      setLoading(false)
    }
    bootstrap()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true)
        setSession(session)
      } else if (event === 'SIGNED_OUT') {
        // Only a real sign-out clears the recovery form. An INITIAL_SESSION /
        // TOKEN_REFRESHED fired around verifyOtp must not drop it.
        setIsRecovery(false)
        setSession(session)
      } else {
        setSession(session)
      }
    })
    return () => { active = false; subscription.unsubscribe() }
  }, [])

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-logo">Crew Dispatch</div>
      <div className="loading-sub">Loading...</div>
    </div>
  )

  // Show password reset form when coming from reset email
  if (isRecovery) return <ResetPasswordForm />

  return (
    <BrowserRouter>
      <Routes>
        {/* /report/edit: no-auth signed link. /report/approve: authenticated
            (the page itself bounces to /login?redirect= when not signed in). */}
        <Route path="/report/approve" element={<ReportApprovePage session={session} />} />
        <Route path="/report/edit" element={<ReportEditPage />} />
        {/* The new hire's own form: no login, the ?t= link token is the only key. */}
        <Route path="/new-hire/start" element={<NewHireSelfPage />} />
        {/* A successful recovery is caught by the isRecovery short-circuit above
            this router; reaching here means the link was invalid/expired/used. */}
        <Route path="/reset-password" element={<ResetLinkInvalid />} />
        <Route path="/login" element={<LoginRoute session={session} />} />
        <Route path="/*" element={session
          ? <Dashboard session={session} isMobile={isMobile} />
          : <Navigate to={loginPath()} />}
        />
      </Routes>
    </BrowserRouter>
  )
}
